import { Navbar, Footer } from './LandingPage';
import { WanderfulHero } from './WanderfulHero';
import { useLocation } from 'react-router-dom';

const useCasesData = {
  "upsc": {
    title: "Evaluation built for the rigor of UPSC Mains.",
    subtitle: "Assess depth of knowledge, policy analysis, and multi-dimensional GS perspectives with civil services standards.",
    section1: {
      title: "Civil Services Specific Features",
      subtitle: "Built to match the demanding assessment standards of India's toughest administrative exam.",
      items: [
        { title: "Directive-Aligned Grading", desc: "Evaluates answers based on the specific directive keywords like 'Critically Analyze', 'Discuss', 'Elucidate', or 'Examine' rather than matching generic keywords." },
        { title: "Multi-Perspective Analysis", desc: "Checks if the aspirant's response covers diverse dimensions—socio-economic, political, historical, environmental, and administrative aspects." },
        { title: "Schematic & Map Recognition", desc: "Detects hand-drawn Indian maps, block diagrams, flowcharts, and mind-maps, scoring them based on accuracy and contextual placement." }
      ]
    },
    bento: {
      title: "Empowering Leading UPSC Academies",
      subtitle: "Accelerate your sessional mock test cycles and deliver IAS-grade feedforward to aspirants.",
      items: [
        { title: "Standardized & Unbiased Checking", desc: "Eliminate discrepancies between different external copy-evaluators, ensuring a single, high-fidelity standard for all mock answer booklets." },
        { title: "Deep Diagnostic Feedforward", desc: "Drafts detailed observations on introduction-body-conclusion transitions, structural coherence, fact-density, and relevance of examples." },
        { title: "Rapid Mock Turnaround", desc: "Process thousands of comprehensive GS mock papers within hours, allowing students to learn from their mistakes while the topic is top of mind." }
      ]
    }
  },
  "jee-neet": {
    title: "High-velocity grading for JEE & NEET Test Series.",
    subtitle: "Identify step-by-step errors in derivations, parse chemical equations, and track physics concepts.",
    section1: {
      title: "PCM/B Evaluation Engine",
      subtitle: "Optimized for complex mathematical formulas, physical chemistry derivations, and biology labeling.",
      items: [
        { title: "Formula Syntax Parser", desc: "Accurately deciphers handwritten mathematical equations, integration/differentiation steps, and organic reaction mechanisms." },
        { title: "Step-Wise Deduction", desc: "Pinpoints exactly where a student slipped up in a derivation, allowing teachers to award partial credit instead of a binary pass/fail." },
        { title: "Diagram & Label Checker", desc: "Verifies physics ray diagrams, electric circuits, and biology cell structure diagrams against standard keys for correct labels." }
      ]
    },
    bento: {
      title: "Built for High-Volume Prep Mock Series",
      subtitle: "Release rank lists and percentile analyses to thousands of aspirants immediately after mock tests.",
      items: [
        { title: "Mega-Batch Evaluation", desc: "Grade up to 50,000 papers in a single day, ensuring consistent speed and quality across state-wide center mock exams." },
        { title: "Silly Mistake Diagnostics", desc: "Automatically flags whether an error was due to a calculation slip, a unit mismatch, or a fundamental conceptual misunderstanding." },
        { title: "Instant Score & Rank Analytics", desc: "Generate batch-wise percentile distributions, question-level difficulty analysis, and instant leaderboards for mock series." }
      ]
    }
  },
  "cbse": {
    title: "CBSE Board Pre-Board & Practice Paper Grading.",
    subtitle: "Ensure strict alignment with the latest CBSE marking schemes, rubrics, and presentation norms.",
    section1: {
      title: "Board Marking Alignment",
      subtitle: "Help school teachers prepare students for board exams by removing grading subjectivity.",
      items: [
        { title: "Official Rubric Integration", desc: "Grades responses strictly according to CBSE marking guidelines, ensuring marks are distributed across key points and formulae." },
        { title: "Word Count & Format Audits", desc: "Checks answer lengths against CBSE guidelines and flags answers that miss underlining key terms or using bullet points." },
        { title: "Step-by-Step Marks Split", desc: "Rewards students for formula writing, calculation steps, and final answers, matching the exact mark splits of final board papers." }
      ]
    },
    bento: {
      title: "Empowering School Educators",
      subtitle: "Reduce administrative fatigue and provide parents with crystal-clear performance logs.",
      items: [
        { title: "The Teacher's Grading Co-pilot", desc: "AI conducts the first pass of checking and drafts contextual corrections, letting teachers focus on final moderation and student mentoring." },
        { title: "PTM-Ready Performance Reports", desc: "Instantly compile student-specific feedback sheets detailing chapter-wise strengths and areas of improvement for Parent-Teacher Meetings." },
        { title: "Classroom Skill Gap Mapping", desc: "Aggregates performance data across sections to highlight exactly which syllabus topics or chapters require remedial classes." }
      ]
    }
  },
  "university": {
    title: "Secure & Scalable University Internal Assessments.",
    subtitle: "Standardize grading across faculties, maintain academic integrity, and automate LMS score updates.",
    section1: {
      title: "Academic Rigor & Security",
      subtitle: "Standardizing grading criteria for diverse higher-education curricula and sessional tests.",
      items: [
        { title: "Multi-Disciplinary Rubrics", desc: "Configure custom grading rubrics for humanities essay structures, engineering proofs, computer science logic, or business reports." },
        { title: "Inter-Evaluator Standardization", desc: "Ensures uniform grading standards across different teaching assistants, sessional checkers, and guest professors." },
        { title: "Audit Trails & Grade Defense", desc: "Maintains a secure, immutable log of how every single mark was allocated to safeguard against grade disputes and appeals." }
      ]
    },
    bento: {
      title: "Scale Semester Assessments Effortlessly",
      subtitle: "Manage the entire grading pipeline online—from handwritten paper upload to LMS ingestion.",
      items: [
        { title: "Academic Integrity Flags", desc: "Scan papers to detect copied content, textbook plagiarism, or AI-generated copy in student sessional assignments." },
        { title: "LMS & Student Portal Sync", desc: "Export graded scores and qualitative feedback directly into Moodle, Canvas, Blackboard, or internal student databases with one click." },
        { title: "Collaborative Moderation", desc: "Let department heads audit randomly sampled papers, adjust grading scales, and finalize results in a unified web console." }
      ]
    }
  },
  "ca-cs": {
    title: "Rigorous Checks for CA, CS, and CMA Mock Tests.",
    subtitle: "Evaluate compliance with professional standards, audit double-entry accuracy, and check legal citations.",
    section1: {
      title: "Professional Exam Benchmarks",
      subtitle: "Built to match the strict, detail-oriented checking style of ICAI and ICSI exams.",
      items: [
        { title: "Financial Statement Auditing", desc: "Grades complex accounting entries, double-entry balance sheets, and ledger adjustments, awarding partial credit for correct steps." },
        { title: "Act & Case Law Citations", desc: "Verifies if the student cited the correct sections of the Companies Act, GST Act, Income Tax Act, and relevant case law precedents." },
        { title: "ICAI/ICSI Marking Rigor", desc: "Applies the precise passing threshold and strict rubric structure expected by professional accounting and secretarial bodies." }
      ]
    },
    bento: {
      title: "Trusted by Top Professional Prep Academies",
      subtitle: "Transform mock tests into clear, structured blueprints for passing on the first attempt.",
      items: [
        { title: "Zero Subjectivity & Fatigue", desc: "Maintains absolute checking precision through long, complex law and taxation papers, avoiding human evaluator fatigue." },
        { title: "Working Notes Evaluation", desc: "Ensures students are checked on working notes and calculations, which carry critical marks in professional accounting exams." },
        { title: "Format & Structure Checks", desc: "Flags papers that deviate from the professional presentation templates, such as audit reports or legal drafting layouts." }
      ]
    }
  }
};

const UseCasesPage = () => {
  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const caseId = pathParts[pathParts.length - 1];

  const data = useCasesData[caseId as keyof typeof useCasesData];

  // If path matches a use case ID, render detail view
  if (data) {
    return (
      <div className="bg-white min-h-screen">
        <Navbar />
        <WanderfulHero 
          title={data.title}
          subtitle={data.subtitle}
          cta1Text="Get Started"
          cta2Text="Talk to our team"
          cta2Link="/support"
        />
        
        {/* Section 1: Specific Features */}
        <section className="py-8 sm:py-12 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="bg-[#f9f9f9] rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-16">
              <div className="text-center mb-12">
                <span className="text-sm font-bold text-slate-900 uppercase tracking-widest bg-gray-100 px-4 py-1.5 rounded-full">Features</span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-black mt-4 mb-4">{data.section1.title}</h2>
                <p className="text-gray-600 max-w-2xl mx-auto font-medium">{data.section1.subtitle}</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                {data.section1.items.map((item, index) => (
                  <div key={index} className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 shadow-sm hover:border-black hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
                    <div className="w-8 h-8 rounded-full bg-gray-100 text-slate-900 flex items-center justify-center font-bold text-sm mb-4">0{index + 1}</div>
                    <h3 className="font-bold text-base sm:text-lg mb-2 text-slate-900">{item.title}</h3>
                    <p className="text-gray-600 text-xs sm:text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Bento Grid (Dark Mode) */}
        <section className="py-8 sm:py-12 md:py-24 bg-black text-white overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-16">
              <span className="text-sm font-bold text-white/50 uppercase tracking-widest">Impact</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-4">{data.bento.title}</h2>
              <p className="text-gray-400 max-w-2xl mx-auto font-medium">{data.bento.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
              {/* Card 1: Large */}
              <div className="md:col-span-2 p-6 sm:p-8 lg:p-10 bg-neutral-900 rounded-2xl sm:rounded-3xl border border-neutral-800 hover:border-neutral-700 transition-all duration-300 flex flex-col justify-between group cursor-pointer">
                <div>
                  <h3 className="font-bold text-2xl mb-3 text-white">{data.bento.items[0].title}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed max-w-md">{data.bento.items[0].desc}</p>
                </div>
                <div className="mt-8 flex items-end justify-between">
                  <span className="text-8xl font-black text-neutral-800 group-hover:text-neutral-700 transition-colors">01</span>
                </div>
              </div>

              {/* Card 2: Tall */}
              <div className="p-6 sm:p-8 lg:p-10 bg-neutral-900 rounded-2xl sm:rounded-3xl border border-neutral-800 hover:border-neutral-700 transition-all duration-300 flex flex-col justify-between group cursor-pointer">
                <div>
                  <h3 className="font-bold text-2xl mb-3 text-white">{data.bento.items[1].title}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed">{data.bento.items[1].desc}</p>
                </div>
                <div className="mt-8 flex items-end justify-between">
                  <span className="text-8xl font-black text-neutral-800 group-hover:text-neutral-700 transition-colors">02</span>
                </div>
              </div>

              {/* Card 3: Wide */}
              <div className="md:col-span-3 p-10 bg-neutral-900 rounded-3xl border border-neutral-800 hover:border-neutral-700 transition-all duration-300 flex flex-col md:flex-row md:items-center justify-between group cursor-pointer">
                <div className="max-w-xl">
                  <h3 className="font-bold text-2xl mb-3 text-white">{data.bento.items[2].title}</h3>
                  <p className="text-neutral-400 text-sm leading-relaxed">{data.bento.items[2].desc}</p>
                </div>
                <div className="mt-8 md:mt-0 flex items-end gap-6 justify-between md:justify-end w-full md:w-auto">
                  <span className="text-8xl font-black text-neutral-800 group-hover:text-neutral-700 transition-colors">03</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing/CTA Emphasis */}
        <section className="py-8 sm:py-12 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="bg-black text-white rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-16 text-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">Ready to Transform Your Evaluation?</h2>
              <p className="text-white/70 mb-8 max-w-2xl mx-auto">Talk to our team to see how Parakh AI can fit into your specific workflow.</p>
              <a href="/support" className="inline-block bg-white text-black text-[15px] font-bold rounded-full px-8 py-3.5 hover:scale-[1.03] transition-all duration-200">
                Talk to Our Team
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    );
  }

  // Default List View (Current Behavior)
  const useCases = [
    {
      id: "upsc",
      title: "UPSC Mains Evaluation",
      description: "Assess long-form General Studies and Optional answers. Grade structure, GS perspective-density, policy relevance, and schematic diagrams with civil services standards.",
      gradient: "from-blue-600 to-cyan-500",
      stats: "Directive-aligned evaluation criteria"
    },
    {
      id: "jee-neet",
      title: "JEE/NEET Test Series Checking",
      description: "Evaluate physics derivations, chemistry equations, and biology diagrams. Track calculation mistakes step-by-step and generate rank analytics instantly.",
      gradient: "from-purple-600 to-pink-500",
      stats: "Step-wise credit deduction & diagnostics"
    },
    {
      id: "cbse",
      title: "CBSE Pre-Board & Practice Papers",
      description: "Align school tests perfectly with official CBSE marking schemes. Track word counts, step marking, and presentation metrics to help students prep for boards.",
      gradient: "from-amber-500 to-red-500",
      stats: "Aligns with the latest CBSE rubrics"
    },
    {
      id: "university",
      title: "University Internal Assessments",
      description: "Standardize semester grading across departments. Streamline internal assessments, prevent grade inflation, maintain audit logs, and sync directly with Moodle/Canvas.",
      gradient: "from-green-500 to-emerald-700",
      stats: "Seamless LMS integration & audit trails"
    },
    {
      id: "ca-cs",
      title: "CA/CS/CMA Mock Prep Checking",
      description: "Grade complex corporate laws, accounting ledgers, and tax calculations. Validate statutory section citations and double-entry balance sheets strictly.",
      gradient: "from-rose-500 to-orange-500",
      stats: "ICAI and ICSI marking scheme alignment"
    }
  ];

  return (
    <div className="bg-white min-h-screen font-sans text-black">
      <Navbar />

      {/* Hero */}
      <section className="pt-20 sm:pt-24 md:pt-32 pb-12 sm:pb-16 md:pb-20 text-center bg-black text-white relative overflow-hidden">
        {/* Glow effect */}
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-80 h-80 bg-blue-600 rounded-full blur-[100px] opacity-20" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10">
          <span className="text-sm font-bold text-white/50 uppercase tracking-widest">Tailored Solutions</span>
          <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mt-4 mb-6">Use Cases for Parakh AI</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto font-medium">Explore how Parakh AI transforms evaluation across different educational domains.</p>
        </div>
      </section>

      {/* Use Cases Grid - Framed Container */}
      <section className="py-8 sm:py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          <div className="bg-gray-50 p-6 sm:p-8 md:p-12 lg:p-16 rounded-2xl sm:rounded-[24px] md:rounded-[40px] border border-gray-100">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
              {useCases.map((uc, index) => (
                <div 
                  key={uc.id}
                  id={uc.id}
                  className={`bg-white rounded-2xl sm:rounded-3xl border border-gray-100 p-6 sm:p-8 md:p-10 hover:shadow-lg transition-all group cursor-pointer flex flex-col justify-between ${index === 0 ? 'md:col-span-2' : ''}`}
                  onClick={() => window.location.href = `/use-cases/${uc.id}`}
                >
                  <div>
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br ${uc.gradient} flex items-center justify-center text-white text-lg sm:text-xl font-bold mb-6`}>
                      0{index + 1}
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-black mb-4">{uc.title}</h3>
                    <p className="text-gray-600 font-medium leading-relaxed">{uc.description}</p>
                  </div>
                  <div className="mt-8 pt-6 border-t border-gray-50 flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-400">{uc.stats}</span>
                    <span className="text-sm font-bold text-black group-hover:underline">Learn More</span>
                  </div>
                </div>
              ))}
            </div>

          </div>
          
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UseCasesPage;
